export const ROUTE_PATHS = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  LEGAL: '/legal',
  DASHBOARD: '/dashboard',
  DASHBOARD_TRANSACTIONS: '/dashboard/transactions',
  DASHBOARD_CARDS: '/dashboard/cards',
  DASHBOARD_TRANSFER: '/dashboard/transfer',
  DASHBOARD_NOTIFICATIONS: '/dashboard/notifications',
  DASHBOARD_PROFILE: '/dashboard/profile',
  DASHBOARD_SETTINGS: '/dashboard/settings',
  ADMIN: '/admin',
  ADMIN_CLIENTS: '/admin/clients',
  ADMIN_TRANSACTIONS: '/admin/transactions',
  ADMIN_NOTIFICATIONS: '/admin/notifications',
  ADMIN_SETTINGS: '/admin/settings',
  ADMIN_PANEL: '/admin-panel',
} as const;

export const NAV_LINKS = [
  { labelKey: 'nav_features', href: '#features' },
  { labelKey: 'nav_services', href: '#services' },
  { labelKey: 'nav_plans', href: '#plans' },
  { labelKey: 'nav_security', href: '#security' },
  { labelKey: 'nav_faq', href: '#faq' },
];

export interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  date_of_birth?: string;
  address?: string;
  city?: string;
  country?: string;
  postcode?: string;
  kyc_status: 'pending' | 'submitted' | 'verified' | 'rejected';
  plan: 'Standard' | 'Plus' | 'Premium' | 'Metal';
  is_admin: boolean;
  id_document_type?: string;
  id_document_front_url?: string;
  selfie_url?: string;
  created_at: string;
  updated_at: string;
}

export interface BankAccount {
  id: string;
  user_id: string;
  account_number: string;
  sort_code: string;
  iban: string;
  bic: string;
  balance: number;
  available_balance: number;
  currency: string;
  status: 'active' | 'frozen';
  created_at: string;
  updated_at: string;
}

export interface Card {
  id: string;
  user_id: string;
  account_id?: string;
  card_number: string;
  card_holder_name: string;
  expiry_month: number;
  expiry_year: number;
  card_type: 'debit' | 'credit';
  card_network: string;
  status: 'active' | 'frozen' | 'blocked';
  is_virtual: boolean;
  created_at: string;
}

export interface Transaction {
  id: string;
  account_id: string;
  user_id?: string;
  type: string;
  amount: number;
  currency: string;
  description: string;
  reference?: string;
  category?: string;
  status: string;
  balance_after?: number;
  created_at: string;
}

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'alert';
  is_read: boolean;
  created_at: string;
}

export const PLANS = [
  {
    name: 'Standard',
    price: '0',
    period: 'zawsze darmowy',
    color: 'border-border',
    highlighted: false,
    features: [
      'Rachunek bieżący GBP',
      'Wirtualna karta debetowa',
      'Przelewy krajowe',
      'Wymiana 5 walut/miesiąc',
      'Wypłaty do £200/miesiąc',
      'Dostęp do aplikacji mobilnej',
    ],
  },
  {
    name: 'Plus',
    price: '9.99',
    period: 'miesięcznie',
    color: 'border-blue-400',
    highlighted: false,
    badge: 'Popularny',
    features: [
      'Wszystko ze Standard',
      'Fizyczna karta debetowa',
      'Wymiana 15 walut/miesiąc',
      'Wypłaty do £400/miesiąc',
      'Ubezpieczenie podróżne',
      'Priorytetowe wsparcie',
    ],
  },
  {
    name: 'Premium',
    price: '19.99',
    period: 'miesięcznie',
    color: 'border-gold',
    highlighted: true,
    badge: 'Najlepsza wartość',
    features: [
      'Wszystko z Plus',
      'Nieograniczona wymiana walut',
      'Darmowe wypłaty z bankomatów na świecie',
      'Inwestycje w akcje bez prowizji',
      'Handel kryptowalutami bez prowizji',
      'Osobisty doradca finansowy',
    ],
  },
  {
    name: 'Metal',
    price: '29.99',
    period: 'miesięcznie',
    color: 'border-zinc-400',
    highlighted: false,
    features: [
      'Wszystko z Premium',
      'Metalowa karta premium',
      'Do 1% cashback na świecie',
      'Ubezpieczenie urządzeń',
      'Dostęp do saloników VIP na lotniskach',
      'Usługa concierge 24/7',
    ],
  },
];

export const FAQ_ITEMS = [
  {
    question: 'Jak otworzyć konto w CYF24 Bank?',
    answer: 'Otwarcie konta zajmuje tylko 5 minut. Wypełnij formularz rejestracyjny danymi osobowymi, prześlij dokumenty weryfikacyjne (paszport lub prawo jazdy), zrób selfie do potwierdzenia tożsamości i wybierz plan. Po weryfikacji dokumentów (zwykle w ciągu 24 godzin) otrzymasz pełny dostęp do konta.',
  },
  {
    question: 'Czy CYF24 Bank jest bezpieczny i regulowany?',
    answer: 'Tak, CYF24 Bank jest regulowany przez Financial Conduct Authority (FCA) i chroniony przez Financial Services Compensation Scheme (FSCS) do kwoty £85,000. Używamy szyfrowania 256-bit SSL, uwierzytelniania biometrycznego i monitorowania transakcji w czasie rzeczywistym.',
  },
  {
    question: 'Jakie opłaty pobiera bank?',
    answer: 'Plan Standard jest całkowicie darmowy z podstawowymi funkcjami. Plus kosztuje £9.99/miesiąc i zawiera fizyczną kartę oraz rozszerzone limity. Premium za £19.99/miesiąc oferuje nieograniczoną wymianę walut i inwestycje bez prowizji. Metal za £29.99/miesiąc zawiera metalową kartę, cashback i dostęp do saloników VIP.',
  },
  {
    question: 'Czy mogę używać konta za granicą?',
    answer: 'Tak! Twoja karta CYF24 działa na całym świecie. Możesz dokonywać płatności w dowolnej walucie z konkurencyjnymi kursami wymiany. Plany Premium i Metal oferują nieograniczoną wymianę walut bez opłat. Możesz również wypłacać gotówkę z bankomatów na całym świecie (limity zależą od planu).',
  },
  {
    question: 'Jak szybko są przetwarzane przelewy?',
    answer: 'Przelewy wewnętrzne między kontami CYF24 są natychmiastowe. Przelewy do innych banków w UK przez Faster Payments zwykle trwają kilka sekund. Międzynarodowe przelewy SWIFT mogą trwać 1-3 dni robocze w zależności od kraju docelowego. Zapewniamy śledzenie wszystkich przelewów w czasie rzeczywistym.',
  },
  {
    question: 'Czy CYF24 Bank oferuje usługi inwestycyjne?',
    answer: 'Tak, plany Premium i Metal zawierają dostęp do naszej platformy inwestycyjnej. Możesz inwestować w akcje, ETF-y, obligacje i kryptowaluty bezpośrednio z konta. Plany Premium i Metal oferują handel bez prowizji. Zapewniamy również narzędzia analizy portfela i spersonalizowane rekomendacje inwestycyjne.',
  },
  {
    question: 'Jakie waluty mogę przechowywać na koncie?',
    answer: 'Twoje główne konto jest w GBP, ale możesz przechowywać i wymieniać ponad 30 walut, w tym EUR, USD, PLN, UAH i więcej. Kursy wymiany są konkurencyjne i przejrzyste. Plany Premium i Metal oferują nieograniczoną wymianę walut bez opłat.',
  },
  {
    question: 'Jak skontaktować się z obsługą klienta?',
    answer: 'Możesz skontaktować się z naszym zespołem wsparcia 24/7 przez czat w aplikacji, e-mail support@cyf24.com lub telefon +44 7520 634020. Klienci Plus, Premium i Metal otrzymują priorytetowe wsparcie z szybszymi czasami odpowiedzi.',
  },
];

export const COUNTRIES = [
  { code: 'GB', name: 'Wielka Brytania' },
  { code: 'PL', name: 'Polska' },
  { code: 'UA', name: 'Ukraina' },
  { code: 'DE', name: 'Niemcy' },
  { code: 'FR', name: 'Francja' },
  { code: 'ES', name: 'Hiszpania' },
  { code: 'IT', name: 'Włochy' },
  { code: 'NL', name: 'Holandia' },
  { code: 'BE', name: 'Belgia' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Szwajcaria' },
  { code: 'SE', name: 'Szwecja' },
  { code: 'NO', name: 'Norwegia' },
  { code: 'DK', name: 'Dania' },
  { code: 'FI', name: 'Finlandia' },
  { code: 'IE', name: 'Irlandia' },
  { code: 'PT', name: 'Portugalia' },
  { code: 'GR', name: 'Grecja' },
  { code: 'CZ', name: 'Czechy' },
  { code: 'RO', name: 'Rumunia' },
  { code: 'HU', name: 'Węgry' },
  { code: 'BG', name: 'Bułgaria' },
  { code: 'HR', name: 'Chorwacja' },
  { code: 'SK', name: 'Słowacja' },
  { code: 'SI', name: 'Słowenia' },
  { code: 'LT', name: 'Litwa' },
  { code: 'LV', name: 'Łotwa' },
  { code: 'EE', name: 'Estonia' },
  { code: 'MT', name: 'Malta' },
  { code: 'CY', name: 'Cypr' },
  { code: 'LU', name: 'Luksemburg' },
  { code: 'IS', name: 'Islandia' },
  { code: 'US', name: 'Stany Zjednoczone' },
  { code: 'CA', name: 'Kanada' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'Nowa Zelandia' },
  { code: 'JP', name: 'Japonia' },
  { code: 'KR', name: 'Korea Południowa' },
  { code: 'SG', name: 'Singapur' },
  { code: 'HK', name: 'Hongkong' },
  { code: 'AE', name: 'Zjednoczone Emiraty Arabskie' },
  { code: 'SA', name: 'Arabia Saudyjska' },
  { code: 'IL', name: 'Izrael' },
  { code: 'TR', name: 'Turcja' },
  { code: 'ZA', name: 'Republika Południowej Afryki' },
  { code: 'BR', name: 'Brazylia' },
  { code: 'MX', name: 'Meksyk' },
  { code: 'AR', name: 'Argentyna' },
  { code: 'CL', name: 'Chile' },
  { code: 'IN', name: 'Indie' },
  { code: 'CN', name: 'Chiny' },
  { code: 'TH', name: 'Tajlandia' },
  { code: 'MY', name: 'Malezja' },
  { code: 'ID', name: 'Indonezja' },
  { code: 'PH', name: 'Filipiny' },
  { code: 'VN', name: 'Wietnam' },
  { code: 'EG', name: 'Egipt' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenia' },
  { code: 'MA', name: 'Maroko' },
  { code: 'TN', name: 'Tunezja' },
  { code: 'RS', name: 'Serbia' },
  { code: 'BA', name: 'Bośnia i Hercegowina' },
  { code: 'MK', name: 'Macedonia Północna' },
  { code: 'AL', name: 'Albania' },
  { code: 'ME', name: 'Czarnogóra' },
  { code: 'MD', name: 'Mołdawia' },
  { code: 'BY', name: 'Białoruś' },
  { code: 'GE', name: 'Gruzja' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AZ', name: 'Azerbejdżan' },
  { code: 'KZ', name: 'Kazachstan' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'KG', name: 'Kirgistan' },
  { code: 'TJ', name: 'Tadżykistan' },
  { code: 'AF', name: 'Afganistan' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'BD', name: 'Bangladesz' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'NP', name: 'Nepal' },
  { code: 'MM', name: 'Mjanma' },
  { code: 'KH', name: 'Kambodża' },
  { code: 'LA', name: 'Laos' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'MV', name: 'Malediwy' },
  { code: 'BN', name: 'Brunei' },
  { code: 'TL', name: 'Timor Wschodni' },
  { code: 'IQ', name: 'Irak' },
  { code: 'IR', name: 'Iran' },
  { code: 'SY', name: 'Syria' },
  { code: 'LB', name: 'Liban' },
  { code: 'JO', name: 'Jordania' },
  { code: 'PS', name: 'Palestyna' },
  { code: 'YE', name: 'Jemen' },
  { code: 'OM', name: 'Oman' },
  { code: 'KW', name: 'Kuwejt' },
  { code: 'BH', name: 'Bahrajn' },
  { code: 'QA', name: 'Katar' },
  { code: 'DZ', name: 'Algieria' },
  { code: 'LY', name: 'Libia' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SS', name: 'Sudan Południowy' },
  { code: 'ET', name: 'Etiopia' },
  { code: 'ER', name: 'Erytrea' },
  { code: 'DJ', name: 'Dżibuti' },
  { code: 'SO', name: 'Somalia' },
  { code: 'UG', name: 'Uganda' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'BI', name: 'Burundi' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'MZ', name: 'Mozambik' },
  { code: 'MW', name: 'Malawi' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
  { code: 'BW', name: 'Botswana' },
  { code: 'NA', name: 'Namibia' },
  { code: 'AO', name: 'Angola' },
  { code: 'CD', name: 'Demokratyczna Republika Konga' },
  { code: 'CG', name: 'Kongo' },
  { code: 'CF', name: 'Republika Środkowoafrykańska' },
  { code: 'CM', name: 'Kamerun' },
  { code: 'TD', name: 'Czad' },
  { code: 'NE', name: 'Niger' },
  { code: 'ML', name: 'Mali' },
  { code: 'MR', name: 'Mauretania' },
  { code: 'SN', name: 'Senegal' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GW', name: 'Gwinea Bissau' },
  { code: 'GN', name: 'Gwinea' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'LR', name: 'Liberia' },
  { code: 'CI', name: 'Wybrzeże Kości Słoniowej' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'GH', name: 'Ghana' },
  { code: 'TG', name: 'Togo' },
  { code: 'BJ', name: 'Benin' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GQ', name: 'Gwinea Równikowa' },
  { code: 'ST', name: 'Wyspy Świętego Tomasza i Książęca' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'SC', name: 'Seszele' },
  { code: 'KM', name: 'Komory' },
  { code: 'MG', name: 'Madagaskar' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'RE', name: 'Reunion' },
  { code: 'YT', name: 'Majotta' },
  { code: 'CV', name: 'Republika Zielonego Przylądka' },
  { code: 'PE', name: 'Peru' },
  { code: 'CO', name: 'Kolumbia' },
  { code: 'VE', name: 'Wenezuela' },
  { code: 'EC', name: 'Ekwador' },
  { code: 'BO', name: 'Boliwia' },
  { code: 'PY', name: 'Paragwaj' },
  { code: 'UY', name: 'Urugwaj' },
  { code: 'GY', name: 'Gujana' },
  { code: 'SR', name: 'Surinam' },
  { code: 'GF', name: 'Gujana Francuska' },
  { code: 'FK', name: 'Falklandy' },
  { code: 'CR', name: 'Kostaryka' },
  { code: 'PA', name: 'Panama' },
  { code: 'NI', name: 'Nikaragua' },
  { code: 'HN', name: 'Honduras' },
  { code: 'SV', name: 'Salwador' },
  { code: 'GT', name: 'Gwatemala' },
  { code: 'BZ', name: 'Belize' },
  { code: 'CU', name: 'Kuba' },
  { code: 'JM', name: 'Jamajka' },
  { code: 'HT', name: 'Haiti' },
  { code: 'DO', name: 'Dominikana' },
  { code: 'PR', name: 'Portoryko' },
  { code: 'TT', name: 'Trynidad i Tobago' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BS', name: 'Bahamy' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent i Grenadyny' },
  { code: 'GD', name: 'Grenada' },
  { code: 'AG', name: 'Antigua i Barbuda' },
  { code: 'DM', name: 'Dominika' },
  { code: 'KN', name: 'Saint Kitts i Nevis' },
  { code: 'AW', name: 'Aruba' },
  { code: 'CW', name: 'Curaçao' },
  { code: 'BM', name: 'Bermudy' },
  { code: 'KY', name: 'Kajmany' },
  { code: 'TC', name: 'Turks i Caicos' },
  { code: 'VG', name: 'Brytyjskie Wyspy Dziewicze' },
  { code: 'VI', name: 'Wyspy Dziewicze Stanów Zjednoczonych' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'MS', name: 'Montserrat' },
  { code: 'GL', name: 'Grenlandia' },
  { code: 'FO', name: 'Wyspy Owcze' },
  { code: 'SJ', name: 'Svalbard i Jan Mayen' },
  { code: 'AX', name: 'Wyspy Alandzkie' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'JE', name: 'Jersey' },
  { code: 'GG', name: 'Guernsey' },
  { code: 'IM', name: 'Wyspa Man' },
  { code: 'AD', name: 'Andora' },
  { code: 'MC', name: 'Monako' },
  { code: 'SM', name: 'San Marino' },
  { code: 'VA', name: 'Watykan' },
  { code: 'LI', name: 'Liechtenstein' },
];

export const FEATURES = [
  {
    icon: 'Zap',
    title: 'Natychmiastowe przelewy',
    description: 'Przelewy między kontami CYF24 są realizowane w czasie rzeczywistym, 24/7.',
  },
  {
    icon: 'Shield',
    title: 'Bezpieczeństwo bankowe',
    description: 'Szyfrowanie 256-bit SSL, uwierzytelnianie biometryczne i ochrona FSCS do £85,000.',
  },
  {
    icon: 'TrendingUp',
    title: 'Inteligentna analityka',
    description: 'Automatyczne kategoryzowanie wydatków i spersonalizowane raporty finansowe.',
  },
  {
    icon: 'Globe',
    title: 'Wiele walut',
    description: 'Przechowuj i wymieniaj ponad 30 walut z konkurencyjnymi kursami.',
  },
  {
    icon: 'Headphones',
    title: 'Wsparcie 24/7',
    description: 'Nasz zespół jest dostępny przez całą dobę przez czat, e-mail i telefon.',
  },
  {
    icon: 'Award',
    title: 'Regulowany przez FCA',
    description: 'Autoryzowany i regulowany przez brytyjski urząd nadzoru finansowego.',
  },
];

export const TESTIMONIALS = [
  {
    name: 'Anna Kowalska',
    role: 'Freelancer',
    avatar: 'AK',
    content: 'CYF24 Bank całkowicie zmienił sposób, w jaki zarządzam finansami. Natychmiastowe przelewy i niskie opłaty za wymianę walut są nieocenione dla mojej pracy z klientami międzynarodowymi.',
    rating: 5,
  },
  {
    name: 'Piotr Nowak',
    role: 'Właściciel firmy',
    avatar: 'PN',
    content: 'Jako właściciel małej firmy potrzebuję banku, który rozumie moje potrzeby. CYF24 oferuje doskonałe narzędzia do zarządzania przepływami pieniężnymi i świetne wsparcie klienta.',
    rating: 5,
  },
  {
    name: 'Maria Wiśniewska',
    role: 'Inwestorka',
    avatar: 'MW',
    content: 'Platforma inwestycyjna jest intuicyjna i oferuje szeroki wybór aktywów. Handel bez prowizji w planie Premium to świetna wartość. Gorąco polecam!',
    rating: 5,
  },
];

export function formatCurrency(amount: number, currency = 'GBP'): string {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(dateStr: string): string {
  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateStr));
}

export function formatIban(iban: string): string {
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

export function maskCardNumber(masked: string): string {
  return masked.replace(/\*/g, '•');
}

export const CATEGORY_LABELS: Record<string, string> = {
  salary: 'Wynagrodzenie',
  food: 'Jedzenie i restauracje',
  transport: 'Transport',
  entertainment: 'Rozrywka',
  shopping: 'Zakupy',
  utilities: 'Media',
  health: 'Zdrowie i medycyna',
  investment: 'Inwestycje',
  transfer: 'Przelew',
  fee: 'Opłata bankowa',
  other: 'Inne',
};